import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import ArrowDown from '@sats-group/icons/16/arrow-down';
import Collapse from '@sats-group/ui-lib/react/collapse/collapse';
import HiddenInput from '@sats-group/ui-lib/react/hidden-input';
import useClickOutside from '@sats-group/ui-lib/react/hooks/use-click-outside';
import useEscape from '@sats-group/ui-lib/react/hooks/use-escape';
import SvgError from '@sats-group/ui-lib/react/icons/16/error';
import SvgInfo from '@sats-group/ui-lib/react/icons/16/info';
import Search from '@sats-group/ui-lib/react/search/search';
import Text from '@sats-group/ui-lib/react/text';

import PhoneInputField from './phone-input-field/phone-input-field';
import type {
  PhoneInput as Props,
  PrefixPickerOption,
} from './phone-input.props';

const PhoneInput: React.FunctionComponent<Props> = ({
  prefixInputName,
  language,
  prefixPicker,
  search,
  title,
  helpText,
  ...inputProps
}) => {
  const defaultDisplayValue =
    prefixPicker.find(option => option.defaultValue)?.displayValue || '';
  const defaultDialCode =
    prefixPicker.find(option => option.defaultValue)?.dialCode || '';
  const [displayValue, setDisplayValue] = useState(defaultDisplayValue);
  const [value, setValue] = useState(defaultDialCode);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<PrefixPickerOption[]>(prefixPicker);
  const [validationError, setValidationError] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchTerm.length) {
      setOptions(
        prefixPicker.filter(option =>
          `${option.name} ${option.dialCode}`
            .toLocaleLowerCase()
            .includes(searchTerm.toLocaleLowerCase())
        )
      );
    } else {
      setOptions(prefixPicker);
    }
  }, [searchTerm]);

  useClickOutside(ref, () => setIsDropdownOpen(false));
  useEscape(() => setIsDropdownOpen(false));

  const handleDialCodeSelect = (selectedOption: PrefixPickerOption) => {
    setDisplayValue(selectedOption.displayValue);
    setValue(selectedOption.dialCode);
    setIsDropdownOpen(false);
  };

  return (
    // NOTE: <fieldset> wrapped in a <div> because of hidden padding appearing in Safari on render without it.
    <div className="phone-input">
      <fieldset className="phone-input__fieldset">
        <HiddenInput name={prefixInputName} value={value} />
        {language && title ? (
          <legend className="phone-input__label">
            <Text theme={Text.themes.emphasis}>
              {title}
              <span className="phone-input__asterisk">*</span>
            </Text>
          </legend>
        ) : null}
        <div
          className={cn('phone-input__wrapper', {
            'phone-input__wrapper--error': validationError,
          })}
        >
          <div className="phone-input__prefix-picker" ref={ref}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={cn('phone-input__trigger', {
                'phone-input__trigger--open': isDropdownOpen,
              })}
            >
              <div className="phone-input__display-value">{displayValue}</div>
              <div
                className={cn('phone-input__icon', {
                  'phone-input__icon--flipped': isDropdownOpen,
                })}
              >
                <ArrowDown />
              </div>
            </button>
            <Collapse
              className="phone-input__dropdown"
              isOpen={isDropdownOpen}
              duration={200}
            >
              <div>
                <div className="phone-input__search">
                  <Search
                    {...search}
                    inputSize={Search.inputSizes.small}
                    value={searchTerm}
                    onChangeFunc={e => setSearchTerm(e)}
                  />
                </div>
                <div className="phone-input__dropdown-list">
                  {options.map(dial => (
                    <button
                      type="button"
                      key={dial.displayValue}
                      onClick={() => handleDialCodeSelect(dial)}
                      className={cn('phone-input__dropdown-item', {
                        'phone-input__dropdown-item--default-list':
                          searchTerm.length < 1,
                      })}
                    >
                      <Text>{dial.emoji}</Text>
                      <Text size={Text.sizes.small}>{dial.name}</Text>
                      <Text
                        className="phone-input__dial-code"
                        size={Text.sizes.small}
                      >
                        {dial.dialCode}
                      </Text>
                    </button>
                  ))}
                </div>
              </div>
            </Collapse>
          </div>
          <div className="phone-input__input">
            <PhoneInputField
              {...inputProps}
              onValidationError={setValidationError}
            />
          </div>
        </div>
        {helpText ? (
          <div className="phone-input__help">
            <SvgInfo />
            <Text size={Text.sizes.interface}>{helpText}</Text>
          </div>
        ) : null}
        {/* NOTE: This is aria-hidden because reporting of validation errors is handled by the browser */}
        {validationError ? (
          <div aria-hidden="true" className="phone-input__error">
            <SvgError />
            <Text size={Text.sizes.interface}>{validationError}</Text>
          </div>
        ) : null}
      </fieldset>
    </div>
  );
};

export default PhoneInput;
